import React, { use, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  Loader,
  X,
  Paperclip,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { IoMdAddCircle } from "react-icons/io";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PurchaseOrderReceiptPDF from "./PurchaseOrderReceipt";
import {
  roundOffNumber,
  roundOffToMaxDecimalPlaces,
} from "utils/utilityFunctions";
import { resolveTransactionFeedback } from "utils/transactionFeedback";
export default function PurchaseOrder({
  rfqId,
  poCode,
  po,
  vendor = "Kailash Khan",
  onClose,
  onApproved,
}) {
  const [sites, setSites] = useState([]);
  const [partPayments, setPartPayments] = useState([]);
  const [selectedSite, setSelectedSite] = useState(po?.site || null);
  const [showCreatePartPayment, setShowCreatePartPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(
    po?.payment_method || "Cash",
  );
  const [paymentStatus, setPaymentStatus] = useState(
    po?.payment_status || "Partial Paid",
  );
  const [materialTransactionCatagoryId, setMaterialTransactionCatagoryId] =
    useState(null);
  const [partPaymentAmount, setPartPaymentAmount] = useState("");
  // No default: user must explicitly choose a payment method
  const [partPaymentMethod, setPartPaymentMethod] = useState("");
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const methodDropdownRef = useRef(null);
  const [partPaymentRemarks, setPartPaymentRemarks] = useState("");
  const [partPaymentFile, setPartPaymentFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendorAdvance, setVendorAdvance] = useState(0);
  const [showPaymentFields, setShowPaymentFields] = useState(false);
  const paymentFieldsRef = useRef(null);
  const partPaymentFormRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isEditable = useMemo(() => po?.status === "open", [po]);
  const { addAlert } = useAlert();

  const getFileNameFromUrl = (url) => {
    if (!url) return "";
    try {
      const cleanUrl = url.split("?")[0];
      const segments = cleanUrl.split("/");
      const lastSegment = segments[segments.length - 1];
      return lastSegment || "Attachment";
    } catch (e) {
      return "Attachment";
    }
  };

  const resolveFileUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const purchaseAmount = po?.lines?.reduce(
    (total, item) => total + item.unit_price * item.quantity,
    0,
  );
  const gstAmount = po?.lines?.reduce(
    (total, item) => total + item.tax_amount,
    0,
  );
  const grandTotal = purchaseAmount + gstAmount;

  const [paidAmount, setPaidAmount] = useState(po?.paid_amount || 0);
  const remainingAmount = useMemo(
    () =>
      Math.max(
        roundOffToMaxDecimalPlaces(grandTotal - Number(paidAmount || 0), 2),
        0,
      ),
    [grandTotal, paidAmount],
  );

  const handleApprovePurchase = async () => {
    const accessToken = localStorage.getItem("access");
    setIsSubmitting(true);
    try {
      const request_body = {
        sites: selectedSite,
        vendor: po?.vendor,
        date: po?.order_date,
        transaction_id: 1,
        transaction_catagory_id: materialTransactionCatagoryId,
        paid_amount: isEditable && !showPaymentFields ? 0 : paidAmount,
        payment_method:
          isEditable && !showPaymentFields ? "Cash" : paymentMethod,
        payment_status:
          isEditable && !showPaymentFields ? "Full Due" : paymentStatus,
        remaining_payment:
          isEditable && !showPaymentFields
            ? grandTotal
            : grandTotal - paidAmount,
        po: po?.id,
        total_price: grandTotal,
        remaining_amount: grandTotal - paidAmount,
        all_materials: po?.lines.map((item) => ({
          po_line: item.id,
          material: item.material,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_amount: item.tax_amount,
          total_price: item.total_price,
        })),
      };
      const response = await fetch(`${BASE_URL}/inventory/create-delivery/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(request_body),
      });

      const data = await response.json().catch(() => null);
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Purchase Order approved successfully",
        errorMessage: "Failed to approve Purchase Order",
      });

      if (!response.ok || !feedback.success) {
        addAlert(feedback.message, "error");
        return;
      }

      addAlert(feedback.message, feedback.alertType);
      if (onApproved) {
        onApproved(data);
      }
      onClose();
    } catch (error) {
      console.error("Error approving Purchase Order:", error);
      addAlert("Failed to approve Purchase Order", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveForWarehouse = async () => {
    const accessToken = localStorage.getItem("access");
    setIsSubmitting(true);
    try {
      const request_body = {
        purchase_order: po?.id,
        paid_amount: isEditable && !showPaymentFields ? 0 : Number(paidAmount),
        payment_method:
          isEditable && !showPaymentFields ? "Cash" : paymentMethod,
        payment_status:
          isEditable && !showPaymentFields ? "Full Due" : paymentStatus,
      };
      const response = await fetch(
        `${BASE_URL}/inventory/purchase-orders/approve-for-warehouse/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(request_body),
        },
      );

      const data = await response.json().catch(() => null);
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Purchase Order approved for warehouse successfully",
        errorMessage: "Failed to approve Purchase Order for warehouse",
      });

      if (!response.ok || !feedback.success) {
        addAlert(feedback.message, "error");
        return;
      }

      addAlert(feedback.message, feedback.alertType);
      if (onApproved) {
        onApproved(data);
      }
      onClose();
    } catch (error) {
      console.error("Error approving Purchase Order for warehouse:", error);
      addAlert("Failed to approve Purchase Order for warehouse", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompletePurchase = async () => {
    setIsSubmitting(true);
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/purchase-orders/complete/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ purchase_order: po?.id }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        addAlert("Purchase Order marked as complete", "success");
        onClose();
      } else {
        addAlert("Failed to mark Purchase Order as complete", "error");
      }
    } catch (error) {
      console.error("Error completing Purchase Order:", error);
      addAlert("Failed to mark Purchase Order as complete", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdjustPaymentFromAdvance = async () => {
    setIsSubmitting(true);
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/adjust-purchase-from-advance/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            po_id: po?.id,
          }),
        },
      );
      if (!response.ok) {
        addAlert("Failed to adjust payment from advance", "error");
      }
      const data = await response.json();
      if (data.status) {
        addAlert("Payment adjusted from advance successfully", "success");
        onClose();
      } else {
        addAlert(
          `Failed to adjust payment from advance: ${data.message}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Error adjusting payment from advance:", error);
      addAlert("Failed to adjust payment from advance", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPartPayment = async () => {
    if (!partPaymentAmount) {
      addAlert("Please enter amount", "error");
      return;
    }
    const amountNumber = Number(partPaymentAmount);
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      addAlert("Please enter a valid amount greater than 0", "error");
      return;
    }
    if (amountNumber > remainingAmount) {
      addAlert("Amount cannot be greater than remaining payment", "error");
      return;
    }
    // Guard: payment method must be explicitly selected
    if (!partPaymentMethod) {
      addAlert("Please select a payment method", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const accessToken = localStorage.getItem("access");

      const formData = new FormData();
      formData.append("purchase_order", po?.id);
      formData.append("paid_amount", amountNumber);
      formData.append("payment_method", partPaymentMethod);
      if (partPaymentRemarks) {
        formData.append("remarks", partPaymentRemarks);
      }
      if (partPaymentFile) {
        formData.append("receipt_image", partPaymentFile);
      }

      const response = await fetch(
        `${BASE_URL}/inventory/create-partpayment/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );
      const data = await response.json().catch(() => null);
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Part payment added successfully",
        errorMessage: "Failed to add part payment",
      });

      if (!response.ok || !feedback.success) {
        addAlert(feedback.message, "error");
        return;
      }

      addAlert(feedback.message, feedback.alertType);
      setPartPaymentAmount("");
      setPartPaymentMethod("");
      setShowMethodDropdown(false);
      setPartPaymentRemarks("");
      setPartPaymentFile(null);
      setShowCreatePartPayment(false);
      if (!feedback.approvalRequired) {
        setPaidAmount((prev) =>
          roundOffToMaxDecimalPlaces(Number(prev || 0) + amountNumber, 2),
        );
      }
      await getPartPayments();
    } catch (error) {
      console.error("Error adding part payment:", error);
      addAlert("Failed to add part payment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSites = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    const res = await fetch(`${BASE_URL}/sites/organizations/${orgId}/sites/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
    const data = await res.json();
    setSites(Array.isArray(data) ? data : []);
  };
  const getPartPayments = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/list-partpayment-of-purchase-order/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ po_id: po?.id }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setPartPayments(data.data || []);
      } else {
        console.error("Failed to fetch part payments");
      }
    } catch (error) {
      console.error("Error fetching part payments:", error);
    }
  };

  useEffect(() => {
    getSites();
    getPartPayments();
  }, []);

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "Material Transaction",
          );
          if (purchaseCatagory) {
            setMaterialTransactionCatagoryId(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  useEffect(() => {
    const fetchVendorAdvance = async () => {
      if (!po?.vendor) {
        setVendorAdvance(0);
        return;
      }
      try {
        const response = await fetch(
          `${BASE_URL}/inventory/get-a-vendor/${po?.vendor}/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );
        const result = await response.json();
        if (response.ok && result?.status && result?.data) {
          setVendorAdvance(Number(result.data.advance_payment) || 0);
        } else {
          setVendorAdvance(0);
        }
      } catch (err) {
        console.error("Error fetching vendor details:", err);
        setVendorAdvance(0);
      }
    };
    fetchVendorAdvance();
  }, [po?.vendor]);

  useEffect(() => {
    if (!showPaymentFields) return;
    const container = scrollContainerRef.current;
    const target = paymentFieldsRef.current;
    if (!container || !target) return;
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const currentScroll = container.scrollTop;
      const offset = targetRect.top - containerRect.top;
      const desiredScrollTop = Math.max(currentScroll + offset - 12, 0);
      container.scrollTo({ top: desiredScrollTop, behavior: "smooth" });
    });
  }, [showPaymentFields]);

  useEffect(() => {
    if (!showCreatePartPayment) return;
    const container = scrollContainerRef.current;
    const target = partPaymentFormRef.current;
    if (!container || !target) return;
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const currentScroll = container.scrollTop;
      const offset = targetRect.top - containerRect.top;
      const desiredScrollTop = Math.max(currentScroll + offset - 12, 0);
      container.scrollTo({ top: desiredScrollTop, behavior: "smooth" });
    });
  }, [showCreatePartPayment]);

  // Close payment method dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        methodDropdownRef.current &&
        !methodDropdownRef.current.contains(e.target)
      ) {
        setShowMethodDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="fixed right-0 top-0 w-2/5 h-full bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center text-[#232323]"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Purchase Order
            </h2>
            <p className="text-xs text-gray-900">{rfqId || "Direct order"}</p>
          </div>
        </div>
        <PDFDownloadLink
          document={
            <PurchaseOrderReceiptPDF
              po={po}
              poCode={poCode}
              vendor={vendor}
              partPayments={partPayments}
            />
          }
          fileName={`Purchase_Order_Receipt_${poCode || "N-A"}.pdf`}
          style={{
            display: po.status === "completed" ? "flex" : "none",
          }}
          className="px-2 py-2 text-sm bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 flex items-center gap-2"
        >
          {({ blob, url, loading, error }) =>
            loading ? "Generating receipt..." : "Download Receipt"
          }
        </PDFDownloadLink>
      </div>

      <div className="flex-1 overflow-y-auto p-4" ref={scrollContainerRef}>
        <div className="pb-3 flex justify-start gap-2">
          <div className="flex items-center text-xs text-gray-500 mb-1">
            PO ID
          </div>
          <div className="flex items-center px-3 justify-between bg-gray-200 rounded-full text-sm text-gray-800 font-medium">
            {poCode || "N/A"}
          </div>
        </div>
        <div className="pb-3">
          <div className="text-xs text-gray-600 mb-1">Vendor</div>
          <div className=" flex items-center justify-between text-sm text-gray-800 font-medium">
            {vendor || "N/A"}
          </div>
        </div>

        {po.site ? (
          <div className="pb-3">
            <div className="text-xs text-gray-600 mb-1">Site</div>
            <span className="text-xs font-medium text-purple-900 bg-purple-100 px-2 py-1 rounded-full">
              {po?.site_name || "N/A"}
            </span>
          </div>
        ) : (
          <div className="pb-3">
            <div className="text-xs text-gray-600 mb-1">Site</div>
            <span className="text-xs font-medium text-yellow-900 bg-yellow-100 px-2 py-1 rounded-full">
              Ordered for Warehouse
            </span>
          </div>
        )}

        <div>
          <div className="text-sm text-[#232323] mb-1">
            Select Delivery Date
          </div>
          <div className="h-10 px-3 flex items-center justify-between bg-gray-200 rounded-md text-sm text-gray-800 font-medium">
            <span>{po.order_date}</span>
            <CalendarDays size={16} className="text-[#232323]" />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-medium text-gray-900">Materials</h3>
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-[#232323] font-medium">
              {po?.lines?.length}
            </span>
          </div>

          <div className="space-y-4">
            {po?.lines?.map((item, idx) => (
              <div key={idx} className="space-y-3">
                <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center justify-between text-sm font-medium text-gray-800">
                  <span>{item.material_name}</span>
                </div>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3 h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm">
                    {item.quantity}
                  </div>
                  <div className="col-span-3 h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm">
                    {item.uom}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <X size={16} className="text-[#232323]" />
                  </div>
                  <div className="col-span-5 h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm">
                    <span className="text-gray-500 mr-1">₹</span>{" "}
                    {item.unit_price}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3">
                    <div className="text-xs text-gray-500 mb-1">GST</div>
                    <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm">
                      {roundOffNumber(
                        (item.tax_amount / (item.unit_price * item.quantity)) *
                        100,
                      )}{" "}
                      <span className="text-gray-500 ml-1">%</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm pt-2">
                  <span className="text-[#232323]">Total Price</span>
                  <span className="font-semibold text-gray-800">
                    ₹ {item.total_price.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 mt-4 pt-4 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[#232323]">Purchase Amount</span>
            <span className="font-semibold text-gray-800">
              ₹ {purchaseAmount.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#232323]">GST Amount</span>
            <span className="font-semibold text-gray-800">
              ₹ {gstAmount.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="font-semibold text-gray-800">Grand Total</span>
          <span className="font-semibold text-gray-800">
            ₹ {grandTotal.toLocaleString("en-IN")}
          </span>
        </div>

        {isEditable && !showPaymentFields ? (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setShowPaymentFields(true);
                setPaymentStatus("Full Due");
                setPaidAmount(0);
              }}
              className="flex items-center gap-2 text-sm font-medium text-[#3B0069] hover:text-purple-900 transition-colors"
            >
              <IoMdAddCircle size={20} />
              <span>Add Payment Details?</span>
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 space-y-4" ref={paymentFieldsRef}>
              {/* <div className="flex items-center gap-3">
                <div className="text-sm text-[#232323] mb-1">Payment Method</div>
                <div className="flex gap-2">
                  {["Cash", "Online", "Cheque"].map((method) => (
                    <button
                      key={method}
                      disabled={!isEditable}
                      onClick={() => setPaymentMethod(method)}
                      className={`px-4 py-1 rounded-full text-sm font-medium ${paymentMethod === method
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                        }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div> */}
              <div className="flex items-center gap-3">
                <div className="text-sm text-[#232323] mb-1">
                  Payment Status
                </div>
                <div className="flex gap-2">
                  {["Partial Paid", "Full Paid", "Full Due"].map((status) => (
                    <button
                      key={status}
                      disabled={!isEditable}
                      onClick={() => {
                        setPaymentStatus(status);
                        if (status === "Full Paid") {
                          setPaidAmount(grandTotal);
                        } else if (status === "Full Due") {
                          setPaidAmount(0);
                        }
                      }}
                      className={`px-4 py-1 rounded-full text-sm font-medium ${paymentStatus === status
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                        }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2 relative">
              {isEditable && showPaymentFields && (
                <button
                  onClick={() => setShowPaymentFields(false)}
                  className="absolute right-0 -top-2 text-gray-400 hover:text-gray-600"
                  title="Remove Payment Details"
                >
                  <X size={16} />
                </button>
              )}
              <div className="flex justify-start gap-3">
                <div className="flex items-center text-sm text-[#232323] mb-1">
                  Paid Amount
                </div>
                {isEditable ? (
                  <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm text-gray-700 relative">
                    <span className="mr-2">₹</span>
                    <input
                      type="text"
                      disabled={!isEditable || paymentStatus !== "Partial Paid"}
                      inputMode="decimal"
                      placeholder="Amount"
                      value={paidAmount}
                      onChange={(e) => {
                        if (
                          e.target.value <= grandTotal &&
                          e.target.value >= 0
                        ) {
                          setPaidAmount(e.target.value);
                        }
                      }}
                      className={`bg-transparent w-full outline-none ${paymentStatus !== "Partial Paid"
                        ? "cursor-not-allowed opacity-50"
                        : ""
                        }`}
                    />
                  </div>
                ) : (
                  <div className="h-10 px-3 rounded-md flex items-center text-base font-medium text-gray-700">
                    <span className="mr-2">₹</span>
                    <span>{paidAmount.toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#232323]">Remaining Payment</span>
                <span className="font-semibold text-gray-800">
                  ₹ {roundOffToMaxDecimalPlaces(grandTotal - paidAmount, 2)}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Part Payments Section */}
        {!isEditable && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Payment History
              </h3>
              <button
                style={{
                  display: remainingAmount <= 0 ? "none" : "flex",
                }}
                onClick={() => setShowCreatePartPayment(true)}
                className="h-8 px-3 bg-[#3B0069] text-white rounded-lg text-sm font-medium flex flex-row items-center justify-center gap-2 hover:bg-purple-900"
              >
                <IoMdAddCircle size={24} color="#ECE6F0" />
                Add Payment
              </button>
            </div>

            {showCreatePartPayment && (
              <div
                className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                ref={partPaymentFormRef}
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Add Part Payment
                  </h4>
                  <button
                    onClick={() => setShowCreatePartPayment(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X size={20} />
                  </button>
                </div>
                {/* Form fields for adding part payment */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Amount
                    </label>
                    {/* <div className="h-10 px-3 bg-white rounded-md flex items-center text-sm text-gray-800 border border-gray-300">
                      <span className="mr-2">₹</span>
                      <input
                        type="number"
                        min="0"
                        value={partPaymentAmount}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === "") {
                            setPartPaymentAmount("");
                            return;
                          }
                          const numeric = Number(next);
                          if (Number.isNaN(numeric) || numeric < 0) return;
                          if (numeric > remainingAmount) {
                            setPartPaymentAmount(String(remainingAmount));
                            return;
                          }
                          setPartPaymentAmount(next);
                        }}
                        max={remainingAmount}
                        className="w-full outline-none"
                        placeholder="Enter amount"
                      />
                    </div> */}
                    <div
                      className={`h-10 px-3 bg-white rounded-md flex items-center text-sm border ${partPaymentAmount > grandTotal - paidAmount
                        ? "border-red-400"
                        : "border-gray-300"
                        } text-gray-800`}
                    >
                      <span className="mr-2">₹</span>
                      <input
                        type="number"
                        min="0"
                        max={grandTotal - paidAmount}
                        value={partPaymentAmount}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          const remaining = grandTotal - paidAmount;
                          if (val > remaining) {
                            setPartPaymentAmount(remaining);
                          } else if (val < 0) {
                            setPartPaymentAmount(0);
                          } else {
                            setPartPaymentAmount(e.target.value);
                          }
                        }}
                        className="w-full outline-none"
                        placeholder="Enter amount"
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        Remaining: ₹ {remainingAmount.toLocaleString("en-IN")}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          setPartPaymentAmount(
                            String(roundOffToMaxDecimalPlaces(remainingAmount, 2)),
                          )
                        }
                        disabled={remainingAmount <= 0}
                        className={`text-xs px-2 py-1 rounded-md border ${remainingAmount > 0
                          ? "text-[#3B0069] border-[#3B0069] hover:bg-[#F5EEF9]"
                          : "text-gray-400 border-gray-300 cursor-not-allowed"
                          }`}
                      >
                        Full Pay
                      </button>
                    </div>
                    {partPaymentAmount > grandTotal - paidAmount && (
                      <p className="text-xs text-red-500 mt-1">
                        Amount cannot exceed remaining payment of ₹
                        {(grandTotal - paidAmount).toLocaleString("en-IN")}
                      </p>
                    )}
                  </div>

                  {/* Payment Method: custom dropdown, no default */}
                  <div className="relative" ref={methodDropdownRef}>
                    <label className="block text-sm text-gray-700 mb-1">
                      Payment Method <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowMethodDropdown((v) => !v)}
                      className={`w-full h-10 px-3 flex items-center justify-between rounded-md text-sm border bg-white ${!partPaymentMethod
                        ? "text-gray-400 border-gray-300"
                        : "text-gray-800 border-gray-300"
                        }`}
                    >
                      <span>
                        {partPaymentMethod || "Select payment method"}
                      </span>
                      <ChevronDown
                        size={16}
                        className="text-gray-500 shrink-0"
                      />
                    </button>
                    {showMethodDropdown && (
                      <div className="absolute z-20 top-full mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg py-1">
                        {["Cash", "Online", "Cheque"].map((method) => (
                          <button
                            key={method}
                            type="button"
                            onClick={() => {
                              setPartPaymentMethod(method);
                              setShowMethodDropdown(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm transition-colors ${partPaymentMethod === method
                              ? "bg-[#3B0069] text-white font-medium"
                              : "text-gray-800 hover:bg-gray-100"
                              }`}
                          >
                            {method}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Remarks (Optional)
                    </label>
                    <textarea
                      className="w-full px-3 py-2 bg-white rounded-md text-sm text-gray-800 font-medium outline-none border border-gray-300"
                      rows={2}
                      value={partPaymentRemarks}
                      onChange={(e) => setPartPaymentRemarks(e.target.value)}
                      placeholder="Enter any remarks"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Attachment (Optional)
                    </label>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-800 cursor-pointer hover:bg-gray-50">
                        <span>Attach file / PDF / Image</span>
                        <input
                          type="file"
                          accept="application/pdf,.pdf,image/*"
                          className="hidden"
                          onChange={(e) =>
                            setPartPaymentFile(
                              e.target.files && e.target.files[0]
                                ? e.target.files[0]
                                : null,
                            )
                          }
                        />
                      </label>
                      {partPaymentFile && (
                        <span className="text-xs text-gray-600 truncate max-w-xs">
                          {partPaymentFile.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      className={`h-10 px-4 py-2 bg-[#3B0069] flex flex-row items-center gap-2 text-white rounded-lg text-sm font-medium hover:bg-purple-900 ${isSubmitting ||
                        !partPaymentMethod ||
                        !partPaymentAmount ||
                        Number(partPaymentAmount) <= 0 ||
                        Number(partPaymentAmount) > remainingAmount
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                        }`}
                      onClick={handleAddPartPayment}
                      disabled={
                        isSubmitting ||
                        !partPaymentMethod ||
                        !partPaymentAmount ||
                        Number(partPaymentAmount) <= 0 ||
                        Number(partPaymentAmount) > remainingAmount
                      }
                    >
                      {isSubmitting && (
                        <Loader
                          size={24}
                          className="animate-spin text-blue-500"
                        />
                      )}
                      Save Payment
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {partPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      ₹ {payment.amount.toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(payment.created_on).toLocaleDateString(
                        "en-IN",
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600">
                      {payment.payment_method}
                    </span>
                    {payment.created_by && (
                      <span className="text-xs text-gray-500">
                        by {payment.created_by}
                      </span>
                    )}
                  </div>
                  {payment.remarks && (
                    <div className="mt-1 text-xs text-gray-500 italic">
                      {payment.remarks}
                    </div>
                  )}
                  {payment.file && (
                    <div className="mt-2">
                      <a
                        href={resolveFileUrl(payment.file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-[11px] font-medium text-purple-800 hover:bg-purple-100"
                      >
                        <Paperclip size={12} />
                        <span className="underline">
                          {getFileNameFromUrl(payment.file)}
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Adjust from Vendor Advance Button */}
        {!isEditable && remainingAmount > 0 && vendorAdvance > 0 && (
          <div className="flex-shrink-0 py-3 mt-2 border-t border-gray-200 flex items-center justify-end gap-3 bg-white">
            <button
              onClick={handleAdjustPaymentFromAdvance}
              disabled={isSubmitting}
              className={`h-10 px-4 py-2 flex flex-row items-center text-sm bg-[#3B0069] text-white rounded-lg font-semibold hover:bg-purple-900 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {isSubmitting && (
                <Loader size={24} className="animate-spin text-blue-500" />
              )}
              Adjust from Vendor Advance
            </button>
          </div>
        )}
      </div>

      {isEditable && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-3 bg-white">
          {!po?.site && !selectedSite ? (
            <button
              onClick={handleApproveForWarehouse}
              className={`h-10 px-4 py-2 text-sm flex flex-row items-center bg-[#3B0069] text-white rounded-lg font-semibold hover:bg-opacity-90 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <Loader size={24} className="animate-spin text-blue-500" />
              )}
              Approve Purchase for Warehouse
            </button>
          ) : (
            <button
              onClick={handleApprovePurchase}
              className={`h-10 px-4 py-2 text-sm flex flex-row items-center bg-[#3B0069] text-white rounded-lg font-semibold hover:bg-opacity-90 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <Loader size={24} className="animate-spin text-blue-500" />
              )}
              Approve & Purchase
            </button>
          )}
        </div>
      )}

      {po?.status === "approved" && !po.site && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-3 bg-white">
          <button
            onClick={() => handleCompletePurchase()}
            className={`h-10 px-4 py-2 text-sm bg-[#3B0069] flex flex-row items-center text-white rounded-lg font-semibold hover:bg-opacity-90 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <Loader size={24} className="animate-spin text-blue-500" />
            )}
            Receive Order
          </button>
        </div>
      )}
    </div>
  );
}
